import { ReactNode, useMemo } from "react";
import "./styles.css";
import { useTranslation } from "../../shared/i18n/i18n-context";

interface SelectableListProps<T> {
    items: T[];
    selectedIds: string[];
    onSelectedIdsChange: (selectedIds: string[]) => void;
    getItemId: (item: T) => string | undefined;
    renderItem: (item: T) => ReactNode;
    listClassName?: string;
    itemClassName?: string;
    emptyState?: ReactNode;
    selectAllLabel?: string;
    bulkActionLabel?: string;
    onBulkAction?: () => void;
    getItemDisabled?: (item: T) => boolean;
    getItemDisabledReason?: (item: T) => string | undefined;
}

export function SelectableList<T>({
    items,
    selectedIds,
    onSelectedIdsChange,
    getItemId,
    renderItem,
    listClassName = "selectable-list",
    itemClassName = "selectable-list-card",
    emptyState,
    selectAllLabel,
    bulkActionLabel,
    onBulkAction,
    getItemDisabled,
    getItemDisabledReason
}: SelectableListProps<T>) {
    const { t } = useTranslation('admin');
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const resolvedSelectAllLabel = selectAllLabel || t('selectAll');
    const resolvedBulkActionLabel = bulkActionLabel || t('deleteSelected');

    const selectableIds = useMemo(() => {
        return items
            .filter((item) => !getItemDisabled?.(item))
            .map((item) => getItemId(item))
            .filter((id): id is string => !!id);
    }, [getItemDisabled, getItemId, items]);

    const allVisibleSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedSet.has(id));
    const selectedVisibleCount = selectableIds.filter((id) => selectedSet.has(id)).length;

    const handleToggleAll = () => {
        const nextSelected = new Set(selectedIds);

        if (allVisibleSelected) {
            selectableIds.forEach((id) => nextSelected.delete(id));
        } else {
            selectableIds.forEach((id) => nextSelected.add(id));
        }

        onSelectedIdsChange(Array.from(nextSelected));
    };

    const handleToggleItem = (item: T) => {
        const id = getItemId(item);

        if (!id || getItemDisabled?.(item)) {
            return;
        }

        const nextSelected = new Set(selectedIds);

        if (nextSelected.has(id)) {
            nextSelected.delete(id);
        } else {
            nextSelected.add(id);
        }

        onSelectedIdsChange(Array.from(nextSelected));
    };

    return (
        <div className="selectable-list-wrapper">
            <div className="selectable-list-toolbar">
                <label className="selectable-list-select-all">
                    <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={selectableIds.length === 0}
                        onChange={handleToggleAll}
                    />
                    <span>{resolvedSelectAllLabel}</span>
                </label>

                <div className="selectable-list-actions">
                    <span className="selectable-list-count">
                        {selectedVisibleCount} {t('selected')}{selectedVisibleCount !== 1 ? t('selectedPluralSuffix') : ""}
                    </span>
                    {onBulkAction && (
                        <button
                            type="button"
                            className="selectable-list-bulk-button"
                            onClick={onBulkAction}
                            disabled={selectedIds.length === 0}
                        >
                            {resolvedBulkActionLabel}
                        </button>
                    )}
                </div>
            </div>

            {items.length === 0 ? (
                emptyState || null
            ) : (
                <div className={listClassName}>
                    {items.map((item) => {
                        const id = getItemId(item);
                        const checked = !!id && selectedSet.has(id);
                        const disabled = !!getItemDisabled?.(item);
                        const disabledReason = getItemDisabledReason?.(item);

                        return (
                            <div
                                key={id}
                                className={`${itemClassName} selectable-list-item ${checked ? "selectable-list-item--selected" : ""} ${disabled ? "selectable-list-item--disabled" : ""}`}
                            >
                                <label className="selectable-list-item-checkbox" title={disabledReason}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => handleToggleItem(item)}
                                        aria-label={t('selectItem')}
                                    />
                                </label>
                                <div className="selectable-list-item-content">
                                    {renderItem(item)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
